import React, { FC } from 'react';

import { Center, Text, Typography } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { Pressable } from 'react-native';
import { TabBar as RNTabBar } from 'react-native-tab-view';

import { AccessibleMainContent } from 'state/ui';
import { theme } from 'styles/configure-theme';

import { ColorOptions, GetTextColorParams, ITabBarProps } from './types';

const getBackgroundColor = (color?: ColorOptions) => {
  return color === 'inverted' ? theme.token('background-active') : 'transparent';
};

const getIndicatorColor = (color?: ColorOptions) => {
  return color === 'inverted' ? Styles.color.white : theme.token('text-default');
};

const getTextColor = ({ color, isDisabled, isFocused }: GetTextColorParams) => {
  if (isDisabled) {
    return theme.token(color === 'inverted' ? 'text-disabled-reversed' : 'text-disabled');
  } else if (color === 'inverted') {
    return Styles.color.white;
  } else if (color === 'secondary') {
    return Styles.color.accent;
  } else if (isFocused) {
    return theme.token('text-default');
  }
  return theme.token('text-default');
};

export const TabBar: FC<React.PropsWithChildren<ITabBarProps>> = ({
  color,
  backgroundColor,
  tabBarTextProps,
  ...props
}) => {
  // NOTE: This ensures more accurate width calculations for the tab components.
  // setting an initialLayout width is essential for the tab bar to calculate its dimensions accurately on the first render.
  // Without it, the layout might appear broken initially until the component re-renders with the correct dimensions.
  const totalTabs = props.navigationState.routes.length;
  const tabViewContainerWidth = props.layout.width;
  const tabWidth = tabViewContainerWidth / totalTabs;

  return (
    <RNTabBar
      {...props}
      onTabPress={ev => {
        if (ev.route.disabled) {
          ev.preventDefault();
        }
      }}
      pressColor="transparent"
      style={{
        backgroundColor: backgroundColor || getBackgroundColor(color),
      }}
      contentContainerStyle={{
        borderBottomColor: theme.token('border-color-default'),
      }}
      tabStyle={{
        width: tabWidth,
        alignItems: 'stretch',
      }}
      indicatorStyle={{
        backgroundColor: getIndicatorColor(color),
        height: 3,
      }}
      renderTabBarItem={({ route, ...props }) => {
        const focused =
          props.navigationState.index ===
          props.navigationState.routes.findIndex(r => r.key === route.key);

        const textColor = getTextColor({ color, isDisabled: route.disabled, isFocused: focused });

        const AccessibleWrapper = route.key === 'nearby' ? AccessibleMainContent : React.Fragment;

        return (
          <Pressable
            onPress={props.onPress}
            style={[{ width: tabWidth, alignItems: 'stretch', paddingVertical: spacing.$4 }]}
            accessibilityRole="tab"
            accessibilityState={{ selected: focused, disabled: !!route.disabled }}
          >
            <AccessibleWrapper>
              <Center>
                <Typography color={textColor} bold={focused} numberOfLines={1}>
                  {route.title}
                </Typography>
                {route.subTitle && (
                  <Text {...tabBarTextProps} color={textColor} bold={focused}>
                    {route.subTitle}
                  </Text>
                )}
              </Center>
            </AccessibleWrapper>
          </Pressable>
        );
      }}
    />
  );
};
